import { Inspector, textStyles, Thumbnail } from '@bynder/design-system';
import { useActiveWidget } from '../../../../queries/guides';
import { ButtonWidget, WIDGET_TYPE, WidgetAlignment } from '../../../../types';
import { useEffect, useState } from 'react';
import { WidgetSettingsHeader } from '../../Styles';
import { useButtonWidgetMutations } from '../../../../mutations/widgets/button';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslate } from '@bynder/localization';
import CustomCssStyleEditor from '../CustomCSSEditor';
import { SettingsAccordion } from '../SettingsAccordion';
import CustomLinkSettings from './ButtonWidgetSettings/CustomLinks';
import LabelEditor from './ButtonWidgetSettings/LabelEditor';
import AlignmentSettings from '../AlignmentSettings';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import { CSS_PROPERTIES, INSPECTOR_DEBOUNCE_DELAY } from '../constants';
import styled from 'styled-components';
import { isDevelopment } from '../../../../constants/app';
import { useCustomCSSEditor } from '../../../../hooks/useCustomCSSEditor';
import ButtonSpacing from './ButtonWidgetSettings/ButtonSpacing';
import { IconLink, IconWidgets } from '@bynder/icons';
import ButtonPadding from './ButtonWidgetSettings/ButtonPadding';
import ButtonText from './ButtonWidgetSettings/ButtonText';
import ButtonBorderRadius from './ButtonWidgetSettings/ButtonBorderRadius';
import ButtonBackground from './ButtonWidgetSettings/ButtonBackground';

export const ButtonWidgetSettings = () => {
  const activeWidget = useActiveWidget() as ButtonWidget;
  const { translate } = useTranslate();
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const button = activeWidget?.buttons[widgetSettings!.widgetIndex!];

  const { editStyle, editWidgetStyleInButtonWidget } =
    useButtonWidgetMutations();
  const [buttonStyle, setButtonStyle] = useState(
    button.style?.stylesheet || '',
  );

  const { getValueFromStyle, addValueToStyle } = useCustomCSSEditor(
    activeWidget.style?.stylesheet,
  );

  const onWidgetStyleChange = (value: string, style: string) => {
    const newStyle = addValueToStyle(style, value);

    editWidgetStyleInButtonWidget.mutate({
      style: newStyle,
    });
  };
  const debouncedSetWidgetStyle = useDebouncedCallback(
    onWidgetStyleChange,
    INSPECTOR_DEBOUNCE_DELAY,
  );

  const handleWidgetStyleChange = (value: string, style: string) => {
    debouncedSetWidgetStyle(value, style);
  };

  const onSetButtonStyle = (style: string) =>
    editStyle.mutate({
      style,
      buttonId: button.id,
      buttonIndex: widgetSettings!.widgetIndex!,
    });
  const debouncedSetButtonStyle = useDebouncedCallback(
    onSetButtonStyle,
    INSPECTOR_DEBOUNCE_DELAY,
  );

  const handleButtonStyleChange = (value: string) => {
    setButtonStyle(value);
    debouncedSetButtonStyle(value);
  };

  useEffect(() => {
    setButtonStyle(button.style?.stylesheet ?? '');
  }, [button.style?.stylesheet, widgetSettings?.widgetIndex]);

  return (
    <>
      <WidgetSettingsHeader widgetType={WIDGET_TYPE.BUTTON} />
      {activeWidget.isLegacy ? (
        <Label>
          This button widget was added during the beta-testing phase and is no
          longer supported, please delete it and re-add the button to use all
          the available features in the new button widget.
        </Label>
      ) : (
        <>
          <LabelEditor text={button?.name.text} />
          <Inspector.Divider />

          <>
            <ButtonPadding
              style={buttonStyle}
              handleStyleChange={handleButtonStyleChange}
            />
            <Inspector.Divider />
            <ButtonText
              style={buttonStyle}
              handleStyleChange={handleButtonStyleChange}
            />
            <Inspector.Divider />
            <ButtonBorderRadius
              style={buttonStyle}
              handleStyleChange={handleButtonStyleChange}
            />
            <Inspector.Divider />
            <ButtonBackground
              style={buttonStyle}
              handleStyleChange={handleButtonStyleChange}
            />
          </>
          <SettingsAccordion
            thumbnail={<Thumbnail icon={<IconLink />} />}
            title={translate('WIDGET_INSPECTOR.BUTTON_CUSTOM_LINK')}
          >
            <CustomLinkSettings
              url={button?.link.url}
              openNewTab={button?.link.openNewTab}
            />
          </SettingsAccordion>
          <SettingsAccordion
            thumbnail={<Thumbnail icon={<IconWidgets />} />}
            subtext={translate('WIDGET_INSPECTOR.BUTTON_ALIGNMENT_SUBTEXT')}
            title={translate('WIDGET_INSPECTOR.BUTTON_ALIGNMENT')}
          >
            <AlignmentSettings
              alignment={
                getValueFromStyle(
                  CSS_PROPERTIES.JUSTIFY_CONTENT,
                ) as WidgetAlignment
              }
              handleChange={handleWidgetStyleChange}
              label={translate('WIDGET_INSPECTOR.CARD_ALIGNMENT')}
            />
            <Inspector.Divider />
            <ButtonSpacing
              gap={getValueFromStyle(CSS_PROPERTIES.GAP) as WidgetAlignment}
              handleChange={handleWidgetStyleChange}
              label={translate('WIDGET_INSPECTOR.BUTTON_GAP')}
            />
          </SettingsAccordion>
          {isDevelopment && (
            <>
              <CustomCssStyleEditor
                style={button.style?.stylesheet}
                label="Individual Button"
              />
              <CustomCssStyleEditor
                style={activeWidget.style.stylesheet}
                label="Widget"
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const Label = styled.label`
  ${textStyles.uiTextM}
`;
